import { TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { TransferState, makeStateKey } from '@angular/core';

import { LoggerService } from '@dibsondance/shared-logging';
import { TranslationLoaderHelper } from './translation-loader.helper';
import { EnvironmentService } from '@dibsondance/shared-environment-base';
import { TranslationCacheService } from './translation-cache.service';
import { I18nHelper } from './i18n.helper';

export interface Translation {
  key: string;
  params?: Record<string, string>;
}

export class TranslationLoaderService extends TranslateLoader {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly transferState = inject(TransferState);
  private readonly translationCache = inject(TranslationCacheService);
  private readonly logger = inject(LoggerService);

  // Static cache for ongoing requests to prevent duplicate HTTP calls
  private static ongoingRequests = new Map<string, Observable<Record<string, string>>>();

  /**
   * Server-level translation cache — persists across SSR requests.
   * Unlike TranslationCacheService (per-injector, browser-only), this cache
   * lives on the class and survives Angular injector destruction between
   * SSR requests. First render loads from HTTP, subsequent renders reuse.
   */
  private static serverTranslationCache = new Map<string, Record<string, string>>();

  /**
   * Clear stale ongoing requests between SSR requests.
   * Called from I18nHelper constructor on the server.
   * NOTE: We intentionally do NOT clear serverTranslationCache here —
   * it must persist across requests for performance.
   */
  static clearOngoingRequests(): void {
    TranslationLoaderService.ongoingRequests.clear();
  }

  /**
   * Preload translations into the server-level cache from disk.
   * Called at server startup (server.bootstrap.ts) BEFORE any SSR request,
   * so the first render doesn't pay the HTTP self-request penalty (~600ms
   * per module×language combo eliminated).
   */
  static preloadToServerCache(cacheKey: string, translations: Record<string, string>): void {
    TranslationLoaderService.serverTranslationCache.set(cacheKey, translations);
  }

  /** Number of entries in the server translation cache (for logging). */
  static get serverCacheSize(): number {
    return TranslationLoaderService.serverTranslationCache.size;
  }
  
  /**
   * Preload translations for a specific module
   * This method integrates with the existing translation loading pipeline
   * @param moduleName The module name (e.g., 'tickets', 'portal')
   * @param language The language code (e.g., 'pl-PL', 'en-GB')
   * @param environmentService Environment service for cache version
   * @returns Observable that completes when translation is loaded
   */
  static preloadModuleTranslations(
    moduleName: string, 
    language: string, 
    environmentService: EnvironmentService
  ): Observable<Record<string, string>> {
    // Create a temporary loader instance to use the existing getTranslation logic
    const httpClient = inject(HttpClient);
    const tempLoader = new TranslationLoaderService(httpClient, moduleName, environmentService);
    
    // Use the existing getTranslation method which handles all the caching, 
    // ongoing requests, and proper marking as loaded
    return tempLoader.getTranslation(language);
  }

  constructor(
    private http: HttpClient,
    private moduleName: string,
    readonly environmentService: EnvironmentService
  ) {
    super();
  }

  getTranslation(lang: string): Observable<Record<string, string>> {
    const stateKey = makeStateKey<Record<string, string>>(`translations-${this.moduleName}-${lang}`);
    const cacheKey = `${this.moduleName}-${lang}`;

    // 0. Check server-level cache (persists across SSR requests).
    // After preloadTranslations() runs at boot, this is the fast path for
    // ALL SSR renders — no HTTP, no file I/O, instant return.
    if (isPlatformServer(this.platformId)) {
      const serverCached = TranslationLoaderService.serverTranslationCache.get(cacheKey);
      if (serverCached) {
        this.transferState.set(stateKey, serverCached);
        I18nHelper.markTranslationLoaded(this.moduleName, lang);
        return of(serverCached);
      }
    }

    // 1. Check in-memory cache first (browser only)
    const cachedTranslation = this.translationCache.getTranslation(cacheKey);
    if (cachedTranslation) {
      // Mark as loaded since it's already cached
      I18nHelper.markTranslationLoaded(this.moduleName, lang);
      return of(cachedTranslation);
    }
    
    // 2. Check if translations were transferred from server (SSR hydration)
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(stateKey)) {
      const translations = this.transferState.get(stateKey, {});
      this.transferState.remove(stateKey);
      // Store in cache for future use
      this.translationCache.setTranslation(cacheKey, translations);
      // Mark as loaded since we got it from transfer state
      I18nHelper.markTranslationLoaded(this.moduleName, lang);
      return of(translations);
    }
    
    // 3. Check if there's already an ongoing request for this translation
    if (TranslationLoaderService.ongoingRequests.has(cacheKey)) {
      this.logger.debug(`Reusing ongoing translation request for: ${cacheKey}`, undefined, 'TranslationLoaderService');
      const ongoingRequest = TranslationLoaderService.ongoingRequests.get(cacheKey)!;
      
      // Ensure we mark as loaded when the ongoing request completes
      return ongoingRequest.pipe(
        tap((translations: Record<string, string>) => {
          I18nHelper.markTranslationLoaded(this.moduleName, lang);
        })
      );
    }
    
    // 4. Load translations via HTTP (both server and browser fallback)
    this.logger.debug(`Loading translations for: ${cacheKey}`, undefined, 'TranslationLoaderService');
    const translations$ = forkJoin([
      this.http.get<Record<string, string>>(
        TranslationLoaderHelper.createModuleI18nPath(
          this.moduleName,
          lang,
          this.environmentService.translationCacheVersion
        )
      ),
      this.http.get<Record<string, string>>(
        TranslationLoaderHelper.createCommonI18nPath(lang, this.environmentService.translationCacheVersion)
      ),
    ]).pipe(
      map(([moduleTranslation, commonTranslation]) => {
        const mergedTranslations = Object.assign({}, commonTranslation, moduleTranslation);
        
        // On server, store translations in TransferState for client hydration
        // and in server-level cache for reuse across SSR requests
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(stateKey, mergedTranslations);
          TranslationLoaderService.serverTranslationCache.set(cacheKey, mergedTranslations);
        }
        
        // On browser, store in cache for future use
        if (isPlatformBrowser(this.platformId)) {
          this.translationCache.setTranslation(cacheKey, mergedTranslations);
        }
        
        // Mark translation as loaded in I18nHelper
        I18nHelper.markTranslationLoaded(this.moduleName, lang);
        
        return mergedTranslations;
      }),
      tap(() => {
        // Remove from ongoing requests when complete
        TranslationLoaderService.ongoingRequests.delete(cacheKey);
      }),
      shareReplay(1) // Share the result among multiple subscribers
    );
    
    // Store as ongoing request to prevent duplicates
    TranslationLoaderService.ongoingRequests.set(cacheKey, translations$);

    return translations$;
  }
}
